import { useCallback, useMemo } from 'react';

import { MensagemInterface } from 'store/reducers/mensagens';

export type VirtualMessageItem = {
  type: 'message' | 'group';
  data: MensagemInterface | MensagemInterface[];
  originalIndex: number;
  messageIds: string[];
};

const QUANTIDADE_MENSAGENS_POR_GRUPO = 3;

export const useMessageGroups = (messages: MensagemInterface[]) => {
  const isEventoAgrupavel = (msg: MensagemInterface) =>
    msg.tipo === 'EVENTO' && msg.evento?.tipo !== 'DATA_MENSAGENS';

  // Cria grupos de mensagens consecutivas do tipo EVENTO com mais de 3 itens
  const criarGruposDeEventos = useCallback(
    (
      items: MensagemInterface[],
    ): Record<number, { mensagens: MensagemInterface[]; ids: string[] }> => {
      const grupos: Record<
        number,
        { mensagens: MensagemInterface[]; ids: string[] }
      > = {};

      let grupoAtual: MensagemInterface[] = [];

      items.forEach((msg, index) => {
        const proximo = items[index + 1];
        const ehEvento = isEventoAgrupavel(msg);

        if (ehEvento) grupoAtual.push(msg);

        const terminouGrupo =
          !proximo || !isEventoAgrupavel(proximo) || index === items.length - 1;

        if (terminouGrupo) {
          if (grupoAtual.length >= QUANTIDADE_MENSAGENS_POR_GRUPO) {
            const startIndex = index - grupoAtual.length + 1;
            grupos[startIndex] = {
              mensagens: [...grupoAtual],
              ids: grupoAtual.map(m => m.id),
            };
          }
          grupoAtual = [];
        }
      });

      return grupos;
    },
    [],
  );

  // Monta a lista de itens virtuais
  const criarItensVirtuais = (
    items: MensagemInterface[],
    grupos: Record<number, { mensagens: MensagemInterface[]; ids: string[] }>,
  ): VirtualMessageItem[] => {
    const virtuais: VirtualMessageItem[] = [];

    for (let i = 0; i < items.length; ) {
      const grupo = grupos[i];

      if (grupo) {
        virtuais.push({
          type: 'group',
          data: grupo.mensagens,
          originalIndex: i,
          messageIds: grupo.ids,
        });
        i += grupo.mensagens.length;
      } else {
        const msg = items[i];
        virtuais.push({
          type: 'message',
          data: msg,
          originalIndex: i,
          messageIds: [msg.id],
        });
        i += 1;
      }
    }

    return virtuais;
  };

  return useMemo(() => {
    const reversed = [...messages].reverse();
    const grupos = criarGruposDeEventos(reversed);
    const virtualized = criarItensVirtuais(reversed, grupos);

    return {
      items: reversed,
      virtualizedItems: virtualized,
    };
  }, [criarGruposDeEventos, messages]);
};
